import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  User,
  Users,
  Shield,
  Building2,
  Briefcase,
  Link,
  MoreHorizontal,
  UserPlus,
  Trash2,
  Calendar,
} from 'lucide-react'
import type { TemplatePermission, AccessScopeType } from '@/lib/api/types'
import { getPermissionLevelConfig } from '@/lib/api/types'
import { templatePermissionService } from '@/lib/api/services/templatePermissions'
import { useToast } from '@/components/ui/use-toast'
import { AddPermissionDialog } from './AddPermissionDialog'

interface PermissionsListProps {
  templateId: string
  permissions: TemplatePermission[]
  canManage: boolean
  onPermissionsChange: () => void
}

const scopeIcons: Record<AccessScopeType, typeof User> = {
  User: User,
  Role: Shield,
  Team: Users,
  Department: Briefcase,
  Everyone: Building2,
  InheritFromHub: Link,
}

export function PermissionsList({
  templateId,
  permissions,
  canManage,
  onPermissionsChange,
}: PermissionsListProps) {
  const [showAddDialog, setShowAddDialog] = useState(false)
  const [permissionToDelete, setPermissionToDelete] =
    useState<TemplatePermission | null>(null)
  const [deleting, setDeleting] = useState(false)
  const { toast } = useToast()

  const handleDelete = async () => {
    if (!permissionToDelete) return

    setDeleting(true)
    try {
      await templatePermissionService.revokePermission(
        templateId,
        permissionToDelete.id
      )
      toast({
        title: 'Permission revoked',
        description: `Access for ${permissionToDelete.scopeName} has been revoked`,
      })
      onPermissionsChange()
    } catch (error) {
      toast({
        title: 'Error',
        description:
          error instanceof Error ? error.message : 'Failed to revoke permission',
        variant: 'destructive',
      })
    } finally {
      setDeleting(false)
      setPermissionToDelete(null)
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
  }

  const activePermissions = permissions.filter((p) => p.isActive)

  return (
    <div className="space-y-4">
      {/* Header with Add button */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          {activePermissions.length} user{activePermissions.length !== 1 && 's'}{' '}
          with access
        </p>
        {canManage && (
          <Button
            size="sm"
            onClick={() => setShowAddDialog(true)}
            className="flex items-center gap-2"
          >
            <UserPlus className="h-4 w-4" />
            Add People
          </Button>
        )}
      </div>

      {/* Permissions list */}
      <div className="space-y-2">
        {activePermissions.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Users className="h-10 w-10 mx-auto mb-3 opacity-50" />
            <p>No one has been granted access yet</p>
            {canManage && (
              <Button
                variant="link"
                size="sm"
                onClick={() => setShowAddDialog(true)}
                className="mt-2"
              >
                Add the first person
              </Button>
            )}
          </div>
        ) : (
          activePermissions.map((permission) => {
            const ScopeIcon = scopeIcons[permission.scopeType] || User
            const levelConfig = getPermissionLevelConfig(
              permission.permissionLevel
            )

            return (
              <div
                key={permission.id}
                className="flex items-center justify-between p-3 rounded-lg border bg-card hover:bg-accent/50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-full bg-muted">
                    <ScopeIcon className="h-4 w-4 text-muted-foreground" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{permission.scopeName}</span>
                      {permission.inheritedFromHubName && (
                        <Badge variant="secondary" className="text-xs">
                          <Link className="h-3 w-3 mr-1" />
                          Inherited
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <span className="capitalize">
                        {permission.scopeType}
                      </span>
                      {permission.expiresAt && (
                        <>
                          <span>•</span>
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            Expires {formatDate(permission.expiresAt)}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <Badge
                    className={`${levelConfig.bgColor} ${levelConfig.textColor} ${levelConfig.darkBgColor} ${levelConfig.darkTextColor}`}
                  >
                    {levelConfig.label}
                  </Badge>

                  {canManage && !permission.inheritedFromHubId && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          onClick={() => setPermissionToDelete(permission)}
                          className="text-destructive focus:text-destructive"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Remove Access
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>
              </div>
            )
          })
        )}
      </div>

      {/* Add Permission Dialog */}
      <AddPermissionDialog
        open={showAddDialog}
        onOpenChange={setShowAddDialog}
        templateId={templateId}
        onSuccess={onPermissionsChange}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        open={!!permissionToDelete}
        onOpenChange={(open) => !open && setPermissionToDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Access</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove access for{' '}
              <strong>{permissionToDelete?.scopeName}</strong>? They will no
              longer be able to access this template.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? 'Removing...' : 'Remove Access'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
